import { Chip, Stack, styled } from '@mui/material';
import { FilterParams } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolRow, useIsMobile } from '@synergy/components-next';
import { ReactElement, useState } from 'react';
import { SliderFilterIcon } from '@synergy/theme-next';
import { useSearchParams } from 'react-router-dom';

import { AllFiltersDrawer } from '../../../components/filter/all-filters-drawer/all-filters-drawer';
import { Filter, FilterPriceChip, FilterPriceSort, FilterToggleChip } from '../../../components/filter';
import { SearchPageFiltersContent } from '../filters/search-page-filters-content';
import { SpecialPriceFilter } from '../filter-toggles/special-price-filter/special-price-filter';
import { articleSearchResultsSelector } from '../../../store/articles-selectors';
import { useAppSelector } from '../../../../store/store';

const StyledChip = styled(Chip)(({ theme }) => ({
    color: theme.palette.grey[800],
    borderColor: theme.palette.grey[400],
    backgroundColor: theme.palette.grey[100],
}));

interface SearchPageFiltersProps {
    filtersEnabled: boolean;
}

export const SearchPageFilters = (props: SearchPageFiltersProps): ReactElement => {
    const articleSearchResults = useAppSelector(articleSearchResultsSelector);
    const [searchParams, setSearchParams] = useSearchParams();
    const isMobile = useIsMobile();
    const [allFiltersOpen, setAllFiltersOpen] = useState(false);
    const [isPriceOpen, setIsPriceOpen] = useState(false);
    const isSpecialOffer = !!searchParams.get(FilterParams.SpecialOffer);
    const isFilterActive =
        searchParams.get(FilterParams.SpecialOffer) ||
        searchParams.get(FilterParams.MinPrice) ||
        searchParams.get(FilterParams.MaxPrice) ||
        searchParams.get(FilterParams.SupplierIds);

    const handleUpdateSearch = (params: URLSearchParams): void => {
        params.delete(FilterParams.Page);
        setSearchParams(params);
    };

    const handlePriceClick = (): void => {
        setAllFiltersOpen(true);
        setIsPriceOpen(true);
    };

    const handleDrawerClose = (): void => {
        setAllFiltersOpen(false);
        setIsPriceOpen(false);
    };

    return (
        <Filter updateSearch={handleUpdateSearch} searchParams={searchParams}>
            <Stack
                display="flex"
                flexDirection={isMobile ? 'column' : 'row'}
                justifyContent={isMobile ? 'flex-start' : ''}
                gap={1}
                flexWrap="wrap"
                sx={{ width: '100%' }}
            >
                {!isMobile && (
                    <StyledChip
                        variant="outlined"
                        label={
                            <FormattedMessage
                                id="app.filter.total"
                                values={{ number: articleSearchResults.total.toString() }}
                            />
                        }
                    />
                )}
                <FilterPriceSort />
                {!isMobile && <FilterPriceChip onPriceClick={handlePriceClick} active={isPriceOpen} />}
                <PoolRow>
                    {!isMobile && <SpecialPriceFilter isSpecialOffer={isSpecialOffer} />}
                    <FilterToggleChip
                        isSelected={props.filtersEnabled}
                        active={allFiltersOpen}
                        outlined={true}
                        color="dark"
                        variant="outlined"
                        icon={<SliderFilterIcon />}
                        label={<FormattedMessage id="app.filter.allFilters" />}
                        onClick={() => setAllFiltersOpen(true)}
                    />
                    {isMobile && (
                        <StyledChip
                            variant="outlined"
                            label={
                                <FormattedMessage
                                    id="app.filter.total"
                                    values={{ number: articleSearchResults.total.toString() }}
                                />
                            }
                            sx={{ flex: 1 }}
                        />
                    )}
                </PoolRow>
            </Stack>

            <AllFiltersDrawer
                open={allFiltersOpen}
                onDrawerClose={handleDrawerClose}
                disabledReset={!isFilterActive}
                totalResults={articleSearchResults.total}
            >
                <SearchPageFiltersContent isPriceOpen={isPriceOpen} />
            </AllFiltersDrawer>
        </Filter>
    );
};
